// src/lib/voiceTranscription.js
//
// Client for the long-running transcription path added to
// supabase/functions/voice-to-text/index.ts (ticket 6b, spec 9.3). A
// separate module from voiceNoteStorage.js on purpose: that one talks to
// the voice-note-storage function (GCS credential), this one talks to
// voice-to-text (Speech-to-Text credential) -- same separation the two
// edge functions themselves keep, carried through to the client.
//
// voice-to-text/index.ts's own header comment explains why this can't be
// a single request-response: a short recording finishes inside the
// function's own inline poll window and comes back as one call, but a
// longer one comes back "processing" with an operation_name and has to be
// asked again. This module is what hides that shape from the UI --
// transcribeVoiceNote() always looks like one call from the caller's side,
// whether it took one round trip or several.

import { supabase } from './supabase';

const FUNCTION_NAME = 'voice-to-text';

// How often the browser re-asks after a "processing" response.
const CLIENT_POLL_INTERVAL_MS = 3000;

// The overall point at which the browser gives up entirely, across every
// round trip combined -- comfortably above the 3-minute recording cap
// itself, since a full-length note is the one most likely to need more
// than a single "processing" round trip. Not a promise that Google will
// always finish by then, just the point past which this module stops
// asking and lets the caller treat it like any other transcription
// failure (9.8: keep the recording, tell the reader, let them type or
// retry) rather than waiting indefinitely.
const CLIENT_MAX_TOTAL_WAIT_MS = 4 * 60 * 1000;

// Same shape as voiceNoteStorage.js's own callVoiceNoteStorage helper --
// see that file's comment for why invokeError.context has to be unwrapped
// rather than trusting invokeError.message alone.
async function callVoiceToText(action, params) {
  const { data, error: invokeError } = await supabase.functions.invoke(FUNCTION_NAME, {
    body: { action, ...params },
  });
  if (invokeError) {
    let message = invokeError.message;
    try {
      const body = await invokeError.context?.json?.();
      if (body?.error) message = body.error;
    } catch {
      // context wasn't JSON or was already consumed -- fall back to
      // invokeError.message rather than masking it with a second error.
    }
    throw new Error(message);
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

// Drives transcribe_object / transcription_status to completion or to
// CLIENT_MAX_TOTAL_WAIT_MS, whichever comes first. Resolves to exactly one
// of:
//   { status: 'ok', transcript }  -- Google returned real text
//   { status: 'empty' }           -- Google heard nothing (not a failure)
//   { status: 'timeout' }         -- gave up waiting, not a thrown error
// Throws only for an actual call failure (not authenticated, ownership
// check failed, a real Google error) -- the caller is expected to handle
// that the same way it handles 'timeout': per spec 9.8, the upload already
// succeeded, so the recording is kept either way and only the transcript
// is missing.
export async function transcribeVoiceNote(objectPath) {
  const deadline = Date.now() + CLIENT_MAX_TOTAL_WAIT_MS;
  let data = await callVoiceToText('transcribe_object', { object_path: objectPath });

  while (data?.status === 'processing') {
    if (Date.now() > deadline) return { status: 'timeout' };
    await new Promise((resolve) => setTimeout(resolve, CLIENT_POLL_INTERVAL_MS));
    data = await callVoiceToText('transcription_status', { operation_name: data.operation_name });
  }

  if (data?.status === 'empty') return { status: 'empty' };
  return { status: 'ok', transcript: data?.transcript || '' };
}
